import api from '@/api';
import type { DataWrapper } from '@/types/api';
import type {
    BackendNotificationType,
    NotificationCounts,
    NotificationGroup,
    NotificationApiItem,
} from '@/types/notification';
import { ApiFactory } from './base/ApiFactory';

/** Raw response shape from GET /notifications (backend wraps in successResponse) */
export interface NotificationListRawResponse {
    success: boolean;
    data: {
        counts: NotificationCounts;
        notifications: NotificationApiItem[] | {
            data: NotificationApiItem[];
            meta: {
                current_page: number;
                from: number;
                last_page: number;
                per_page: number;
                to: number;
                total: number;
            };
        };
    };
}

class NotificationService extends ApiFactory<unknown> {
    constructor() {
        super('/notifications', api);
    }

    async getNotifications(params?: {
        type?: BackendNotificationType;
        page?: number;
        per_page?: number;
    }): Promise<NotificationListRawResponse> {
        return this.get<NotificationListRawResponse>(this.resource, params);
    }

    async list(params?: {
        type?: string;
        page?: number;
        per_page?: number;
    }): Promise<{
        success: boolean;
        data: {
            groups: NotificationGroup[];
            counts: NotificationCounts;
            pagination: {
                current_page: number;
                last_page: number;
                per_page: number;
                total: number;
                from: number;
                to: number;
            };
        };
    }> {
        return this.get(`${this.resource}/`, params);
    }

    async getCounts(): Promise<DataWrapper<NotificationCounts>> {
        return this.get<DataWrapper<NotificationCounts>>(`${this.resource}/counts`);
    }

    async markOneAsRead(id: string): Promise<{ success: boolean; data: null; message?: string }> {
        return this.patch<{ success: boolean; data: null; message?: string }>(`${this.resource}/${id}/read`, {});
    }

    async markAllAsRead(): Promise<{ success: boolean; data: null; message?: string }> {
        return this.patch<{ success: boolean; data: null; message?: string }>(`${this.resource}/read-all`, {});
    }
}

export const notificationService = new NotificationService();
